import * as React from 'react';
import classnames from 'classnames';
import { Button, IconButton, Modal } from '@ixicare/ui';
import { X, type LucideIcon } from 'lucide-react';
import { t } from '~i18n';
import styles from './ActionBar.module.css';

// Chips and buttons collapse independently, against the bar's own width rather than the
// viewport's: the sidebar beside it can be expanded, a rail or gone, so the same viewport leaves
// the bar anywhere from ~270px less than full width to all of it.
// - Chips hide once the bar can't fit the title + actions + all chips. That
//   width scales with the chip count, so it's computed per render.
// - Buttons drop to icons only when the no-chips layout (title + actions) no longer
//   fits
const NARROW_BASE_PX = 540; // title + actions + close + paddings/gaps (no chips)
const CHIP_PX = 130; // approx rendered width of one chip (MAC address, monospace)

export interface BarAction {
  id: string;
  label: string;
  /** Icon-font glyph name (ignored when `lucide` is set). */
  icon?: string;
  /** Lucide override; takes precedence over `icon`. */
  lucide?: LucideIcon;
  variant?: 'primary' | 'ghost' | 'danger';
  /** A divider sets this action's group off from the actions after it. */
  group?: string;
  disabled?: boolean;
  /** When set, clicking opens a confirm modal and `onClick` runs on confirm. */
  confirm?: { title: string; body: string; details?: React.ReactNode; action?: string };
  onClick: () => void;
}

interface Props {
  /** Left-side label (e.g. "Selected device:" or "3 selected"). */
  title: React.ReactNode;
  /** Left-side list, rendered as chips. */
  chips?: React.ReactNode[];
  actions: BarAction[];
  /** Right-side rendering: text buttons (default) or icon buttons. */
  actionStyle?: 'button' | 'icon';
  /**
   * Message between the title and the actions, for the standing consequence of the last action.
   * Belongs in the bar rather than in the page: the bar is fixed, so a notice anywhere else can
   * sit off-screen while the button that caused it stays visible.
   */
  notice?: React.ReactNode;
  /** Optional clear/dismiss control on the far left. */
  onClose?: () => void;
  /** Accessible label for the close control. */
  closeLabel?: string;
  /** Fixed-bar left offset in px. Defaults to the app sidebar's current width. */
  offsetLeft?: number;
}

const buttonTypeFor = (variant?: BarAction['variant']) => {
  if (variant === 'danger') return 'error' as const;
  if (variant === 'primary') return 'primary-flat' as const;
  return undefined;
};

const ActionBar: React.FC<Props> = ({
  title,
  chips,
  actions,
  actionStyle = 'button',
  notice,
  onClose,
  closeLabel,
  offsetLeft,
}) => {
  const [confirming, setConfirming] = React.useState<BarAction | null>(null);

  const barRef = React.useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const node = barRef.current;
    if (!node) return;
    // Measured before paint, so the bar does not render labelled and then snap to icons. The
    // border box, since NARROW_BASE_PX counts the bar's own padding.
    const observer = new ResizeObserver(([entry]) => setBarWidth(entry.target.getBoundingClientRect().width));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Chip threshold grows with the selection; button threshold is fixed (no chips).
  const chipsHidden = barWidth !== null && barWidth <= NARROW_BASE_PX + (chips?.length ?? 0) * CHIP_PX;
  const compactButtons = barWidth !== null && barWidth <= NARROW_BASE_PX;
  const effectiveStyle = actionStyle === 'icon' || compactButtons ? 'icon' : 'button';

  const runAction = (action: BarAction) => {
    if (action.disabled) return;
    if (action.confirm) {
      setConfirming(action);
      return;
    }
    action.onClick();
  };

  const barStyle =
    offsetLeft !== undefined ? ({ '--actionbar-offset-left': `${offsetLeft}px` } as React.CSSProperties) : undefined;

  return (
    <>
      <div ref={barRef} className={styles.actionbar} style={barStyle}>
        {onClose && (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label={closeLabel}>
            <X size="1em" strokeWidth={2} />
          </button>
        )}
        <div className={styles.actionbarLeft}>
          <b>{title}</b>
          {!chipsHidden && !!chips?.length && (
            <span className={styles.chips}>
              {chips.map((chip, i) => (
                <span key={i} className={styles.chip}>
                  {chip}
                </span>
              ))}
            </span>
          )}
        </div>
        {notice && <div className={styles.notice}>{notice}</div>}
        <div className={styles.actionbarActions}>
          {actions.map((action, i) => (
            <React.Fragment key={action.id}>
              {!!actions[i - 1]?.group && actions[i - 1].group !== action.group && <span className={styles.divider} />}
              {effectiveStyle === 'icon' ? (
                <span
                  className={classnames(
                    styles.iconButton,
                    action.variant === 'danger' && styles.danger,
                    action.disabled && styles.iconButtonDisabled
                  )}
                  title={action.label}
                  aria-disabled={action.disabled}
                >
                  <IconButton
                    icon={action.lucide ? undefined : action.icon}
                    lucide={action.lucide}
                    smallIcon={false}
                    onClick={() => runAction(action)}
                  />
                </span>
              ) : (
                <Button
                  className={action.variant === 'danger' ? styles.danger : undefined}
                  buttonType={buttonTypeFor(action.variant)}
                  onClick={() => runAction(action)}
                  disabled={action.disabled}
                  type="button"
                  icon={action.icon}
                  lucide={action.lucide}
                >
                  {action.label}
                </Button>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {confirming?.confirm && (
        <Modal onClose={() => setConfirming(null)} center>
          <div className={styles.confirmModal}>
            <h3>{confirming.confirm.title}</h3>
            <p>{confirming.confirm.body}</p>
            {confirming.confirm.details}
            <div className={styles.confirmActions}>
              <Button type="button" onClick={() => setConfirming(null)}>
                {t('device-config.actions.cancel')}
              </Button>
              <Button
                type="button"
                buttonType={buttonTypeFor(confirming.variant) ?? 'error'}
                onClick={() => {
                  const action = confirming;
                  setConfirming(null);
                  action.onClick();
                }}
              >
                {confirming.confirm.action ?? t('device-config.actions.reset-confirm.action')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ActionBar;
